<template>
<div>
    <section class="pt-12">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-5 col-lg-6 order-md-2">
                    <img src="assets/img/illustrations/illustration-3.png" alt="Instagram MCP Server for Claude AI" class="img-fluid mb-6 mb-md-0">
                </div>
                <div class="col-12 col-md-7 col-lg-6 order-md-1">
                    <h1 class="display-4">
                        Instagram MCP Server
                        <span class="text-primary d-block fs-2">Query Instagram Data with Claude AI</span>
                    </h1>
                    <p class="fs-lg text-gray-700 mb-4">
                        Connect <strong>Claude AI</strong> to <strong>Instagram data</strong> using <strong>Model Context Protocol (MCP)</strong>. Search users by demographics, analyze engagement, get posts and comments — all with natural language queries. 31 tools for Instagram analytics, face analysis, and audience research.
                    </p>
                    <p class="text-success text-center text-md-start fw-bold mb-4" v-show="!$state.context.id">
                        <i class="fe fe-gift me-2"></i>{{ $state.context.free_requests }} free requests trial after Telegram verification
                    </p>
                </div>
            </div>
        </div>
    </section>

    <section class="py-4 py-md-6 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-4">Quick Start</h2>

                    <h4>1. Sign in to DataLikers</h4>
                    <p class="text-muted">
                        <span v-if="$state.context.id">
                            You're signed in. For Claude.ai web — no token needed (OAuth handles auth automatically). For Claude Desktop / Cursor / Codex / Zed — copy your access key from <router-link to="/tokens">Tokens</router-link>.
                        </span>
                        <span v-else>
                            <router-link to="/registration">Register</router-link> or <router-link to="/login">login</router-link>. Claude.ai web users sign in via OAuth and don't need a token. Desktop / CLI clients use a long-lived access key from the Tokens page.
                        </span>
                    </p>

                    <!-- Tab navigation -->
                    <ul class="nav nav-tabs mt-4" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#claude-web" type="button" role="tab">
                                Claude.ai
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#claude-desktop" type="button" role="tab">
                                Claude Desktop
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#claude-code" type="button" role="tab">
                                Claude Code
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#cursor" type="button" role="tab">
                                Cursor
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#codex" type="button" role="tab">
                                Codex
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#zed" type="button" role="tab">
                                Zed
                            </button>
                        </li>
                    </ul>

                    <!-- Tab content -->
                    <div class="tab-content pt-4">
                        <!-- Claude.ai Web Tab -->
                        <div class="tab-pane fade show active" id="claude-web" role="tabpanel">
                            <div class="alert alert-info py-2 small">
                                <strong>Note:</strong> Remote MCP servers are available on Pro, Max, Team, and Enterprise plans.
                            </div>

                            <ul class="nav nav-pills mcp-auth-pills mb-3" role="tablist">
                                <li class="nav-item" role="presentation">
                                    <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#claude-web-oauth" type="button" role="tab">
                                        OAuth <span class="badge bg-success ms-1">Recommended</span>
                                    </button>
                                </li>
                                <li class="nav-item" role="presentation">
                                    <button class="nav-link" data-bs-toggle="tab" data-bs-target="#claude-web-legacy" type="button" role="tab">
                                        Access Key <span class="badge bg-secondary ms-1">Legacy</span>
                                    </button>
                                </li>
                            </ul>

                            <div class="tab-content">
                            <!-- Claude.ai OAuth (recommended) -->
                            <div class="tab-pane fade show active" id="claude-web-oauth" role="tabpanel">
                                <p class="text-muted small">
                                    <i class="fe fe-shield me-1"></i>
                                    OAuth 2.1 flow: Claude.ai redirects you to <code>datalikers.com</code>, you approve once, and the connector receives a scoped token automatically. No copy-paste, no token management.
                                </p>
                            <div class="row g-3 claude-web-steps">
                                <div class="col-12 col-md-6">
                                    <div class="step-card">
                                        <div class="step-card-header"><span class="step-num">1</span><h6>Open Settings</h6></div>
                                        <p class="step-card-text">Go to <a href="https://claude.ai" target="_blank">claude.ai</a>, click your profile icon (bottom-left) and pick <strong>Settings</strong>.</p>
                                        <div class="mcp-screenshot">
                                            <div class="screenshot-placeholder">
                                                <div class="screenshot-content">
                                                    <div class="screenshot-sidebar">
                                                        <div class="screenshot-logo">Claude</div>
                                                        <div class="screenshot-menu-item"></div>
                                                        <div class="screenshot-menu-item"></div>
                                                        <div class="screenshot-menu-item"></div>
                                                        <div class="screenshot-profile">
                                                            <div class="screenshot-avatar"></div>
                                                            <div class="screenshot-dropdown">
                                                                <div class="screenshot-dropdown-item highlight">Settings</div>
                                                                <div class="screenshot-dropdown-item"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="screenshot-main"></div>
                                                </div>
                                                <div class="screenshot-label">Click profile → Settings</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="step-card">
                                        <div class="step-card-header"><span class="step-num">2</span><h6>Navigate to Connectors</h6></div>
                                        <p class="step-card-text">In Settings, click <strong>Connectors</strong> in the sidebar.</p>
                                        <div class="mcp-screenshot">
                                            <div class="screenshot-placeholder">
                                                <div class="screenshot-content settings-view">
                                                    <div class="screenshot-settings-sidebar">
                                                        <div class="screenshot-settings-item">Profile</div>
                                                        <div class="screenshot-settings-item">Appearance</div>
                                                        <div class="screenshot-settings-item highlight">Connectors</div>
                                                        <div class="screenshot-settings-item">Privacy</div>
                                                    </div>
                                                    <div class="screenshot-settings-main">
                                                        <div class="screenshot-title">Connectors</div>
                                                        <div class="screenshot-connector-list">
                                                            <div class="screenshot-connector"></div>
                                                            <div class="screenshot-connector"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="screenshot-label">Settings → Connectors</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="step-card">
                                        <div class="step-card-header"><span class="step-num">3</span><h6>Add Custom Connector</h6></div>
                                        <p class="step-card-text">Scroll down and click <strong>Add custom connector</strong>.</p>
                                        <div class="mcp-screenshot">
                                            <div class="screenshot-placeholder">
                                                <div class="screenshot-content settings-view">
                                                    <div class="screenshot-settings-sidebar">
                                                        <div class="screenshot-settings-item">Profile</div>
                                                        <div class="screenshot-settings-item highlight">Connectors</div>
                                                    </div>
                                                    <div class="screenshot-settings-main">
                                                        <div class="screenshot-title">Connectors</div>
                                                        <div class="screenshot-connector-list">
                                                            <div class="screenshot-connector"></div>
                                                            <div class="screenshot-connector"></div>
                                                        </div>
                                                        <button class="screenshot-btn highlight">+ Add custom connector</button>
                                                    </div>
                                                </div>
                                                <div class="screenshot-label">Click "Add custom connector"</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="step-card">
                                        <div class="step-card-header"><span class="step-num">4</span><h6>Enter MCP Server URL</h6></div>
                                        <p class="step-card-text">Paste the OAuth-enabled DataLikers URL and click <strong>Add</strong>. Claude.ai will detect OAuth and start the auth flow.</p>
                                        <CodeCopy :code="oauthMcpUrl" title="Copy URL" message="MCP URL copied to clipboard" />
                                        <div class="mcp-screenshot mt-2">
                                            <div class="screenshot-placeholder">
                                                <div class="screenshot-content modal-view">
                                                    <div class="screenshot-modal">
                                                        <div class="screenshot-modal-title">Add custom connector</div>
                                                        <div class="screenshot-input-group">
                                                            <label>Server URL</label>
                                                            <div class="screenshot-input">https://mcp.datalikers.com/oauth</div>
                                                        </div>
                                                        <div class="screenshot-modal-buttons">
                                                            <button class="screenshot-btn-secondary">Cancel</button>
                                                            <button class="screenshot-btn-primary">Add</button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="screenshot-label">Enter URL and click "Add"</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="step-card">
                                        <div class="step-card-header"><span class="step-num">5</span><h6>Approve in OAuth Consent</h6></div>
                                        <p class="step-card-text">Claude.ai redirects to <code>datalikers.com</code>. Sign in (if needed), then click <strong>Allow</strong> on the consent screen to grant <code>mcp:read</code> scope.</p>
                                        <div class="mcp-screenshot">
                                            <div class="screenshot-placeholder">
                                                <div class="screenshot-content modal-view">
                                                    <div class="screenshot-modal screenshot-modal-consent">
                                                        <div class="screenshot-modal-title">Authorize Claude.ai</div>
                                                        <div class="screenshot-consent-body">
                                                            <div class="screenshot-consent-line"><strong>Claude.ai</strong> wants to access:</div>
                                                            <div class="screenshot-scope">
                                                                <span class="screenshot-check">✓</span>
                                                                <span>Read Instagram &amp; TikTok data (<code>mcp:read</code>)</span>
                                                            </div>
                                                        </div>
                                                        <div class="screenshot-modal-buttons">
                                                            <button class="screenshot-btn-secondary">Deny</button>
                                                            <button class="screenshot-btn-primary">Allow</button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="screenshot-label">Click "Allow" on datalikers.com</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12">
                                    <div class="step-card step-card-final">
                                        <div class="step-card-header"><span class="step-num">6</span><h6>Start Using</h6></div>
                                        <p class="step-card-text mb-0">Open a new conversation — Claude now has access to all DataLikers tools. Manage or revoke the grant anytime under <router-link to="/settings/connected-apps">Connected Apps</router-link>.</p>
                                    </div>
                                </div>
                            </div>
                            </div>

                            <!-- Claude.ai Legacy access key -->
                            <div class="tab-pane fade" id="claude-web-legacy" role="tabpanel">
                                <p class="text-muted small">
                                    <i class="fe fe-key me-1"></i>
                                    Legacy flow — long-lived access key in the URL. Still supported for backward compatibility. Prefer OAuth for new setups: better revocation, scoped permissions, no token in browser settings.
                                </p>
                                <h5 class="mt-3">Step 1: Get your Access Key</h5>
                                <p class="text-muted">
                                    <span v-if="$state.context.id">Copy your access key from the <router-link to="/tokens">Tokens</router-link> page.</span>
                                    <span v-else><router-link to="/registration">Register</router-link> or <router-link to="/login">login</router-link> to get an access key.</span>
                                </p>

                                <h5 class="mt-4">Step 2: Add Custom Connector</h5>
                                <p class="text-muted">
                                    In Claude.ai → Settings → Connectors → <strong>Add custom connector</strong>, paste the legacy MCP URL (note: trailing slash):
                                </p>
                                <CodeCopy :code="mcpUrl" title="Copy URL" message="MCP URL copied to clipboard" />

                                <h5 class="mt-4">Step 3: Add Authorization Header</h5>
                                <p class="text-muted">
                                    In the custom-connector dialog, expand <strong>Advanced</strong> and add a header:
                                </p>
                                <CodeCopy :code="legacyHeader" lang="text" title="Copy header" message="Header copied to clipboard" />
                            </div>
                            </div>
                        </div>

                        <!-- Claude Desktop Tab -->
                        <div class="tab-pane fade" id="claude-desktop" role="tabpanel">
                            <h5>Step 1: Open Configuration File</h5>
                            <p class="text-muted">
                                Open your Claude Desktop configuration file:<br>
                                <code>~/.config/claude/claude_desktop_config.json</code> (Linux/Mac) or<br>
                                <code>%APPDATA%\Claude\claude_desktop_config.json</code> (Windows)
                            </p>

                            <h5 class="mt-4">Step 2: Add MCP Server Configuration</h5>
                            <p class="text-muted">
                                Add the following configuration:
                            </p>
                            <CodeCopy :code="configJson" lang="json" title="Copy config" message="Config copied to clipboard" />

                            <h5 class="mt-4">Step 3: Restart Claude Desktop</h5>
                            <p class="text-muted">
                                After saving the configuration, restart Claude Desktop to load the MCP server.
                            </p>
                        </div>

                        <!-- Claude Code Tab -->
                        <div class="tab-pane fade" id="claude-code" role="tabpanel">
                            <h5>Run a single command</h5>
                            <p class="text-muted">
                                Add DataLikers MCP server to your Claude Code CLI with one command:
                            </p>
                            <CodeCopy :code="claudeCodeCommand" lang="bash" title="Copy command" message="Command copied to clipboard" />

                            <h5 class="mt-4">Scope options</h5>
                            <p class="text-muted">
                                <code>-s project</code> — available only in current project directory<br>
                                <code>-s user</code> — available globally for all projects
                            </p>

                            <h5 class="mt-4">Start using</h5>
                            <p class="text-muted">
                                After adding, run <code>claude</code> and ask questions like:<br>
                                <em>"Find top fitness influencers in New York"</em>
                            </p>
                        </div>

                        <!-- Cursor Tab -->
                        <div class="tab-pane fade" id="cursor" role="tabpanel">
                            <h5>Step 1: Open MCP Configuration</h5>
                            <p class="text-muted">
                                Open Cursor Settings (<code>Cmd+,</code> on Mac, <code>Ctrl+,</code> on Windows/Linux),
                                go to <strong>Features → MCP Servers</strong>, then click <strong>"Add new MCP server"</strong>.
                            </p>

                            <h5 class="mt-4">Step 2: Configure Server</h5>
                            <p class="text-muted">
                                Or manually edit the MCP config file:<br>
                                <code>~/.cursor/mcp.json</code> (Mac/Linux) or <code>%USERPROFILE%\.cursor\mcp.json</code> (Windows)
                            </p>
                            <CodeCopy :code="cursorConfig" lang="json" title="Copy config" message="Config copied to clipboard" />

                            <h5 class="mt-4">Step 3: Restart Cursor</h5>
                            <p class="text-muted">
                                Restart Cursor to apply the configuration. The MCP server will appear in the Agent panel.
                            </p>
                        </div>

                        <!-- Codex Tab -->
                        <div class="tab-pane fade" id="codex" role="tabpanel">
                            <h5>Step 1: Open Codex Configuration</h5>
                            <p class="text-muted">
                                Open the OpenAI Codex CLI config file:<br>
                                <code>~/.codex/config.toml</code> (Mac/Linux/Windows)
                            </p>

                            <h5 class="mt-4">Step 2: Add MCP Server</h5>
                            <p class="text-muted">
                                Codex CLI talks to MCP servers over stdio, so we use <code>mcp-remote</code> to bridge to our HTTP server:
                            </p>
                            <CodeCopy :code="codexConfig" lang="toml" title="Copy config" message="Config copied to clipboard" />

                            <h5 class="mt-4">Step 3: Restart Codex</h5>
                            <p class="text-muted">
                                Restart your Codex session. The DataLikers tools will appear automatically — try
                                <em>"Find top 10 fitness influencers in Brazil"</em> to test the connection.
                            </p>

                            <h5 class="mt-4">Alternative: one-line install</h5>
                            <p class="text-muted">
                                If your Codex build supports the <code>codex mcp add</code> command:
                            </p>
                            <CodeCopy :code="codexCommand" lang="bash" title="Copy command" message="Command copied to clipboard" />
                        </div>

                        <!-- Zed Tab -->
                        <div class="tab-pane fade" id="zed" role="tabpanel">
                            <h5>Step 1: Open Settings</h5>
                            <p class="text-muted">
                                Open Zed settings file:<br>
                                <code>~/.config/zed/settings.json</code> (Mac/Linux)
                            </p>

                            <h5 class="mt-4">Step 2: Add Context Server</h5>
                            <p class="text-muted">
                                Add the DataLikers MCP server to your settings:
                            </p>
                            <CodeCopy :code="zedConfig" lang="json" title="Copy config" message="Config copied to clipboard" />

                            <h5 class="mt-4">Step 3: Restart Zed</h5>
                            <p class="text-muted">
                                Restart Zed to load the MCP server. Use the Assistant panel to interact with Instagram data.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="py-4 py-md-6 terminal-section">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-2 terminal-section-title">Real Queries, Real Data</h2>
                    <p class="terminal-section-subtitle mb-5">Live examples of MCP requests run against the DataLikers production database. Just ask in natural language — Claude picks the right tool, builds the query, and returns structured results.</p>

                    <!-- Scenario 1: Korean influencers -->
                    <div class="terminal-demo mb-4">
                        <div class="terminal-header">
                            <span class="terminal-dot dot-red"></span>
                            <span class="terminal-dot dot-yellow"></span>
                            <span class="terminal-dot dot-green"></span>
                            <span class="terminal-title">influencer outreach</span>
                        </div>
                        <div class="terminal-body">
                            <div class="terminal-line">
                                <span class="terminal-prompt">&gt;</span>
                                <span class="terminal-user">Find 10 verified Korean male creators aged 28–50 with a public email and 1M+ followers — I'm planning a regional brand activation.</span>
                            </div>
                            <div class="terminal-tool">
                                <span class="terminal-arrow">⏺</span>
                                <span class="terminal-tool-name">search_users_by_demographics</span><span class="terminal-tool-args">(country=<span class="ta-str">"KR"</span>, gender=<span class="ta-str">"man"</span>, min_age=<span class="ta-num">28</span>, max_age=<span class="ta-num">50</span>, has_email=<span class="ta-bool">true</span>, min_followers=<span class="ta-num">1000000</span>, is_verified=<span class="ta-bool">true</span>, limit=<span class="ta-num">10</span>)</span>
                            </div>
                            <div class="terminal-result">
                                <div class="terminal-table">
                                    <div class="tr th">
                                        <span class="td c-user">username</span>
                                        <span class="td c-name">full_name</span>
                                        <span class="td c-num">followers</span>
                                        <span class="td c-num">age</span>
                                        <span class="td c-email">public_email</span>
                                    </div>
                                    <div class="tr"><span class="td c-user">@ai.work</span><span class="td c-name">정근욱</span><span class="td c-num">1.73M</span><span class="td c-num">39</span><span class="td c-email">akumazanai@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@deukie_______</span><span class="td c-name">Youngdeuk Kwon</span><span class="td c-num">1.45M</span><span class="td c-num">31</span><span class="td c-email">godjewoos@hanmail.net</span></div>
                                    <div class="tr"><span class="td c-user">@rico_9c</span><span class="td c-name">Ku Hyunho 具賢皓</span><span class="td c-num">1.16M</span><span class="td c-num">33</span><span class="td c-email">hyunhoku5@hanmail.net</span></div>
                                    <div class="tr"><span class="td c-user">@johnnykyunghwo</span><span class="td c-name">Johnny Kyunghwo Sheldrick</span><span class="td c-num">1.13M</span><span class="td c-num">28</span><span class="td c-email">johnny@night.co</span></div>
                                    <div class="tr"><span class="td c-user">@chada6in</span><span class="td c-name">Cha Dabin</span><span class="td c-num">1.11M</span><span class="td c-num">34</span><span class="td c-email">ywstudio.contact@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@sianfaceyoga</span><span class="td c-name">시안페이스요가 유인영</span><span class="td c-num">1.01M</span><span class="td c-num">29</span><span class="td c-email">yusian0406@gmail.com</span></div>
                                    <div class="tr muted"><span class="td c-user">…</span><span class="td c-name">+ 4 more rows</span><span class="td c-num"></span><span class="td c-num"></span><span class="td c-email"></span></div>
                                </div>
                            </div>
                            <div class="terminal-status"><span class="terminal-ok">✓</span> 10 results · billed 10 reqs</div>
                        </div>
                    </div>

                    <!-- Scenario 2: US fashion women -->
                    <div class="terminal-demo mb-4">
                        <div class="terminal-header">
                            <span class="terminal-dot dot-red"></span>
                            <span class="terminal-dot dot-yellow"></span>
                            <span class="terminal-dot dot-green"></span>
                            <span class="terminal-title">fashion campaign</span>
                        </div>
                        <div class="terminal-body">
                            <div class="terminal-line">
                                <span class="terminal-prompt">&gt;</span>
                                <span class="terminal-user">I need US-based women aged 22–32 in fashion, 10k+ followers, with a contact email. Give me their face age and emotion too — I want energetic vibes.</span>
                            </div>
                            <div class="terminal-tool">
                                <span class="terminal-arrow">⏺</span>
                                <span class="terminal-tool-name">search_users_by_demographics</span><span class="terminal-tool-args">(country=<span class="ta-str">"US"</span>, gender=<span class="ta-str">"woman"</span>, min_age=<span class="ta-num">22</span>, max_age=<span class="ta-num">32</span>, hashtag=<span class="ta-str">"fashion"</span>, has_email=<span class="ta-bool">true</span>, min_followers=<span class="ta-num">10000</span>, limit=<span class="ta-num">10</span>)</span>
                            </div>
                            <div class="terminal-result">
                                <div class="terminal-table">
                                    <div class="tr th">
                                        <span class="td c-user">username</span>
                                        <span class="td c-name">full_name</span>
                                        <span class="td c-num">followers</span>
                                        <span class="td c-num">age</span>
                                        <span class="td c-tag">emotion</span>
                                        <span class="td c-email">public_email</span>
                                    </div>
                                    <div class="tr"><span class="td c-user">@alisha</span><span class="td c-name">Alisha Marie</span><span class="td c-num">3.38M</span><span class="td c-num">32</span><span class="td c-tag"><span class="emo neutral">neutral</span></span><span class="td c-email">alishamarie@mattermediagroup.com</span></div>
                                    <div class="tr"><span class="td c-user">@lillykofficial</span><span class="td c-name">Lilliana Ketchman</span><span class="td c-num">3.11M</span><span class="td c-num">28</span><span class="td c-tag"><span class="emo sad">sad</span></span><span class="td c-email">lillyketchman@zoho.com</span></div>
                                    <div class="tr"><span class="td c-user">@byzareefa</span><span class="td c-name">Zareefa Ahmed-Arije</span><span class="td c-num">2.60M</span><span class="td c-num">29</span><span class="td c-tag"><span class="emo neutral">neutral</span></span><span class="td c-email">mgmt@ammubeauty.com</span></div>
                                    <div class="tr"><span class="td c-user">@nicholeciotti</span><span class="td c-name">Nichole Ciotti</span><span class="td c-num">2.47M</span><span class="td c-num">29</span><span class="td c-tag"><span class="emo neutral">neutral</span></span><span class="td c-email">nichole@select.co</span></div>
                                    <div class="tr"><span class="td c-user">@daianesodre</span><span class="td c-name">Daiane 🇧🇷</span><span class="td c-num">1.54M</span><span class="td c-num">26</span><span class="td c-tag"><span class="emo neutral">neutral</span></span><span class="td c-email">daiane@daianesodre.com</span></div>
                                    <div class="tr"><span class="td c-user">@gaabymejia</span><span class="td c-name">Gaby Mejia</span><span class="td c-num">1.11M</span><span class="td c-num">26</span><span class="td c-tag"><span class="emo neutral">neutral</span></span><span class="td c-email">inquiry.gmejia@gmail.com</span></div>
                                    <div class="tr muted"><span class="td c-user">…</span><span class="td c-name">+ 4 more rows</span><span class="td c-num"></span><span class="td c-num"></span><span class="td c-tag"></span><span class="td c-email"></span></div>
                                </div>
                            </div>
                            <div class="terminal-status"><span class="terminal-ok">✓</span> 10 results · billed 10 reqs</div>
                        </div>
                    </div>

                    <!-- Scenario 3: Restaurants with phone -->
                    <div class="terminal-demo mb-4">
                        <div class="terminal-header">
                            <span class="terminal-dot dot-red"></span>
                            <span class="terminal-dot dot-yellow"></span>
                            <span class="terminal-dot dot-green"></span>
                            <span class="terminal-title">sales prospecting</span>
                        </div>
                        <div class="terminal-body">
                            <div class="terminal-line">
                                <span class="terminal-prompt">&gt;</span>
                                <span class="terminal-user">Pull restaurant business accounts that have BOTH a phone number AND a public email — sales is calling tomorrow.</span>
                            </div>
                            <div class="terminal-tool">
                                <span class="terminal-arrow">⏺</span>
                                <span class="terminal-tool-name">get_business_users</span><span class="terminal-tool-args">(category=<span class="ta-str">"Restaurant"</span>, has_phone=<span class="ta-bool">true</span>, has_email=<span class="ta-bool">true</span>, limit=<span class="ta-num">10</span>)</span>
                            </div>
                            <div class="terminal-result">
                                <div class="terminal-table">
                                    <div class="tr th">
                                        <span class="td c-user">username</span>
                                        <span class="td c-name">name</span>
                                        <span class="td c-city">city</span>
                                        <span class="td c-num">followers</span>
                                        <span class="td c-phone">phone</span>
                                        <span class="td c-email">email</span>
                                    </div>
                                    <div class="tr"><span class="td c-user">@kfcindia_official</span><span class="td c-name">KFC India</span><span class="td c-city">—</span><span class="td c-num">875K</span><span class="td c-phone">+91 80 4275 4444</span><span class="td c-email">feedback@kfcindia.co.in</span></div>
                                    <div class="tr"><span class="td c-user">@themarshmallowco</span><span class="td c-name">The Marshmallow Co.</span><span class="td c-city">Wyong, NSW</span><span class="td c-num">416K</span><span class="td c-phone">+61 435 214 310</span><span class="td c-email">hello@themarshmallow.co</span></div>
                                    <div class="tr"><span class="td c-user">@303inthecut</span><span class="td c-name">303 In the Cut</span><span class="td c-city">Las Vegas, NV</span><span class="td c-num">322K</span><span class="td c-phone">+1 702 786 7278</span><span class="td c-email">303inthecut@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@nammos.dubai</span><span class="td c-name">Nammos Dubai</span><span class="td c-city">Dubai, UAE</span><span class="td c-num">259K</span><span class="td c-phone">+971 58 121 0000</span><span class="td c-email">reservations@nammos.ae</span></div>
                                    <div class="tr"><span class="td c-user">@frostbistro</span><span class="td c-name">Frost Bistro &amp; Bar</span><span class="td c-city">Atlanta, GA</span><span class="td c-num">189K</span><span class="td c-phone">+1 404 963 5495</span><span class="td c-email">info@frostbistro.com</span></div>
                                    <div class="tr"><span class="td c-user">@bludsosbbq</span><span class="td c-name">Bludso's BBQ</span><span class="td c-city">Los Angeles, CA</span><span class="td c-num">172K</span><span class="td c-phone">+1 323 931 2583</span><span class="td c-email">bludsosbbq@gmail.com</span></div>
                                    <div class="tr muted"><span class="td c-user">…</span><span class="td c-name">+ 4 more rows</span><span class="td c-city"></span><span class="td c-num"></span><span class="td c-phone"></span><span class="td c-email"></span></div>
                                </div>
                            </div>
                            <div class="terminal-status"><span class="terminal-ok">✓</span> 10 results · billed 10 reqs</div>
                        </div>
                    </div>

                    <!-- Scenario 4: Brazil fitness -->
                    <div class="terminal-demo mb-4">
                        <div class="terminal-header">
                            <span class="terminal-dot dot-red"></span>
                            <span class="terminal-dot dot-yellow"></span>
                            <span class="terminal-dot dot-green"></span>
                            <span class="terminal-title">supplement brand outreach</span>
                        </div>
                        <div class="terminal-body">
                            <div class="terminal-line">
                                <span class="terminal-prompt">&gt;</span>
                                <span class="terminal-user">Brazilian male fitness creators, 22–40 years old, have used #fitness in posts, contactable by email — give me their follower counts so I can tier them.</span>
                            </div>
                            <div class="terminal-tool">
                                <span class="terminal-arrow">⏺</span>
                                <span class="terminal-tool-name">search_users_by_demographics</span><span class="terminal-tool-args">(country=<span class="ta-str">"BR"</span>, gender=<span class="ta-str">"man"</span>, min_age=<span class="ta-num">22</span>, max_age=<span class="ta-num">40</span>, hashtag=<span class="ta-str">"fitness"</span>, has_email=<span class="ta-bool">true</span>, min_followers=<span class="ta-num">20000</span>, limit=<span class="ta-num">10</span>)</span>
                            </div>
                            <div class="terminal-result">
                                <div class="terminal-table">
                                    <div class="tr th">
                                        <span class="td c-user">username</span>
                                        <span class="td c-name">full_name</span>
                                        <span class="td c-num">followers</span>
                                        <span class="td c-num">age</span>
                                        <span class="td c-tag">tier</span>
                                        <span class="td c-email">public_email</span>
                                    </div>
                                    <div class="tr"><span class="td c-user">@arthurpicoli</span><span class="td c-name">Arthur Picoli</span><span class="td c-num">3.61M</span><span class="td c-num">28</span><span class="td c-tag"><span class="tier mega">mega</span></span><span class="td c-email">arthur@mmestrategiadeimagem.com.br</span></div>
                                    <div class="tr"><span class="td c-user">@oangelosilva</span><span class="td c-name">Angelo Silva</span><span class="td c-num">1.48M</span><span class="td c-num">33</span><span class="td c-tag"><span class="tier mega">mega</span></span><span class="td c-email">oangelosilvacontato@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@lais.cintra</span><span class="td c-name">Laís Cintra</span><span class="td c-num">611K</span><span class="td c-num">35</span><span class="td c-tag"><span class="tier macro">macro</span></span><span class="td c-email">lais.cintra@grupofarol.com</span></div>
                                    <div class="tr"><span class="td c-user">@vini.caldi</span><span class="td c-name">Vinícius Caldi</span><span class="td c-num">237K</span><span class="td c-num">32</span><span class="td c-tag"><span class="tier macro">macro</span></span><span class="td c-email">contato.vinicaldi@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@danillosilveira.hpt</span><span class="td c-name">Danillo Silveira</span><span class="td c-num">147K</span><span class="td c-num">32</span><span class="td c-tag"><span class="tier mid">mid</span></span><span class="td c-email">danillo.mormaii1@gmail.com</span></div>
                                    <div class="tr"><span class="td c-user">@fernaobattistoni</span><span class="td c-name">Fernao Battistoni</span><span class="td c-num">55K</span><span class="td c-num">29</span><span class="td c-tag"><span class="tier mid">mid</span></span><span class="td c-email">contato@fernaobattistoni.com</span></div>
                                    <div class="tr muted"><span class="td c-user">…</span><span class="td c-name">+ 4 more rows</span><span class="td c-num"></span><span class="td c-num"></span><span class="td c-tag"></span><span class="td c-email"></span></div>
                                </div>
                            </div>
                            <div class="terminal-status"><span class="terminal-ok">✓</span> 10 results · billed 10 reqs</div>
                        </div>
                    </div>

                    <!-- Scenario 5: Japan beauty -->
                    <div class="terminal-demo mb-4">
                        <div class="terminal-header">
                            <span class="terminal-dot dot-red"></span>
                            <span class="terminal-dot dot-yellow"></span>
                            <span class="terminal-dot dot-green"></span>
                            <span class="terminal-title">beauty PR list</span>
                        </div>
                        <div class="terminal-body">
                            <div class="terminal-line">
                                <span class="terminal-prompt">&gt;</span>
                                <span class="terminal-user">Build me a PR seeding list of Japanese women, 20–30 years old, who post #beauty content, with a contact email. I'll filter to micro-influencers.</span>
                            </div>
                            <div class="terminal-tool">
                                <span class="terminal-arrow">⏺</span>
                                <span class="terminal-tool-name">search_users_by_demographics</span><span class="terminal-tool-args">(country=<span class="ta-str">"JP"</span>, gender=<span class="ta-str">"woman"</span>, min_age=<span class="ta-num">20</span>, max_age=<span class="ta-num">30</span>, hashtag=<span class="ta-str">"beauty"</span>, has_email=<span class="ta-bool">true</span>, min_followers=<span class="ta-num">10000</span>, limit=<span class="ta-num">10</span>)</span>
                            </div>
                            <div class="terminal-result">
                                <div class="terminal-table">
                                    <div class="tr th">
                                        <span class="td c-user">username</span>
                                        <span class="td c-name">full_name</span>
                                        <span class="td c-num">followers</span>
                                        <span class="td c-num">age</span>
                                        <span class="td c-tag">verified</span>
                                        <span class="td c-email">public_email</span>
                                    </div>
                                    <div class="tr"><span class="td c-user">@93_5world</span><span class="td c-name">935❤︎</span><span class="td c-num">114K</span><span class="td c-num">28</span><span class="td c-tag"><span class="badge-no">—</span></span><span class="td c-email">ku_minmin935@yahoo.co.jp</span></div>
                                    <div class="tr"><span class="td c-user">@ayanodagane</span><span class="td c-name">アヤノダガネ</span><span class="td c-num">72.9K</span><span class="td c-num">28</span><span class="td c-tag"><span class="badge-no">—</span></span><span class="td c-email">creator_info@wows.co.jp</span></div>
                                    <div class="tr"><span class="td c-user">@yu10mama</span><span class="td c-name">Sana Ooshiro</span><span class="td c-num">12.8K</span><span class="td c-num">23</span><span class="td c-tag"><span class="badge-yes">✓</span></span><span class="td c-email">sana.company.0815@icloud.com</span></div>
                                </div>
                            </div>
                            <div class="terminal-status"><span class="terminal-ok">✓</span> 3 results · billed 3 reqs</div>
                        </div>
                    </div>

                    <p class="terminal-section-footer small mt-3 text-center mb-0">
                        Same MCP server. Same access key. Different question every time.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <section class="py-4 py-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-4">Available Tools</h2>

                    <!-- Users -->
                    <h5 class="mt-4 text-primary">Users</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_user_by_username</code></td>
                                    <td>Get Instagram user profile by username</td>
                                </tr>
                                <tr>
                                    <td><code>get_user_by_id</code></td>
                                    <td>Get Instagram user profile by user ID</td>
                                </tr>
                                <tr>
                                    <td><code>get_users_by_ids</code></td>
                                    <td>Get up to 100 Instagram user profiles by pk in a single call</td>
                                </tr>
                                <tr>
                                    <td><code>search_users</code></td>
                                    <td>Search users by username or full name</td>
                                </tr>
                                <tr>
                                    <td><code>get_users_by_hashtag</code></td>
                                    <td>Get users who used specific hashtag</td>
                                </tr>
                                <tr>
                                    <td><code>get_users_by_location</code></td>
                                    <td>Get users from specific location/city</td>
                                </tr>
                                <tr>
                                    <td><code>get_top_users</code></td>
                                    <td>Get top users by follower count</td>
                                </tr>
                                <tr>
                                    <td><code>get_business_users</code></td>
                                    <td>Get business accounts by category</td>
                                </tr>
                                <tr>
                                    <td><code>search_users_by_demographics</code></td>
                                    <td>Search users by demographics: age, gender, race, emotion, country, city, location, hashtag</td>
                                </tr>
                                <tr>
                                    <td><code>get_user_photo</code></td>
                                    <td>Get and display user's profile photo by username or user ID</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Media -->
                    <h5 class="mt-4 text-primary">Posts & Reels</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_user_medias</code></td>
                                    <td>Get posts/reels by specific user</td>
                                </tr>
                                <tr>
                                    <td><code>get_media_by_code</code></td>
                                    <td>Get post/reel by Instagram shortcode (from URL)</td>
                                </tr>
                                <tr>
                                    <td><code>get_top_medias</code></td>
                                    <td>Get top posts by likes or comments</td>
                                </tr>
                                <tr>
                                    <td><code>search_media_captions</code></td>
                                    <td>Search posts by caption text</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Comments -->
                    <h5 class="mt-4 text-primary">Comments</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_comments_by_user</code></td>
                                    <td>Get comments made by specific user</td>
                                </tr>
                                <tr>
                                    <td><code>search_comments</code></td>
                                    <td>Search comments by text</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Hashtags -->
                    <h5 class="mt-4 text-primary">Hashtags</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_hashtag_info</code></td>
                                    <td>Get hashtag statistics and info</td>
                                </tr>
                                <tr>
                                    <td><code>search_hashtags</code></td>
                                    <td>Search hashtags by name</td>
                                </tr>
                                <tr>
                                    <td><code>get_top_hashtags</code></td>
                                    <td>Get most popular hashtags by media count</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Locations -->
                    <h5 class="mt-4 text-primary">Locations</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_location_info</code></td>
                                    <td>Get location details by ID</td>
                                </tr>
                                <tr>
                                    <td><code>search_locations</code></td>
                                    <td>Search locations by name or city</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Stories -->
                    <h5 class="mt-4 text-primary">Stories</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_user_stories</code></td>
                                    <td>Get stories by specific user</td>
                                </tr>
                                <tr>
                                    <td><code>get_story_by_id</code></td>
                                    <td>Get story by ID</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Highlights -->
                    <h5 class="mt-4 text-primary">Highlights</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_user_highlights</code></td>
                                    <td>Get highlights by specific user</td>
                                </tr>
                                <tr>
                                    <td><code>get_highlight_by_id</code></td>
                                    <td>Get highlight by ID</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Music Tracks -->
                    <h5 class="mt-4 text-primary">Music Tracks</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>search_tracks</code></td>
                                    <td>Search music tracks by title or artist</td>
                                </tr>
                                <tr>
                                    <td><code>get_track_by_id</code></td>
                                    <td>Get music track by ID</td>
                                </tr>
                                <tr>
                                    <td><code>get_top_tracks</code></td>
                                    <td>Get popular music tracks</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Analytics & Advanced -->
                    <h5 class="mt-4 text-primary">Analytics & Advanced</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>get_user_engagement</code></td>
                                    <td>Calculate user engagement metrics (avg likes, comments, engagement rate)</td>
                                </tr>
                                <tr>
                                    <td><code>get_stats</code></td>
                                    <td>Get database statistics (total users, posts, etc.)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Metadata -->
                    <h5 class="mt-4 text-primary">Metadata</h5>
                    <div class="table-responsive">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>list_business_categories</code></td>
                                    <td>List the Instagram business profile categories indexed in our database (with approximate account counts). Use before passing <code>category</code> to <code>get_business_users</code> / <code>search_users_by_demographics</code> so the filter matches Instagram's taxonomy.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="py-4 py-md-6 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-4">Example Usage</h2>
                    <p class="text-muted">Once configured, you can ask Claude questions like:</p>

                    <div class="row">
                        <div class="col-md-6">
                            <h6 class="mt-3 text-primary">Users & Profiles</h6>
                            <ul class="text-muted small">
                                <li>"Get profile information for @nike"</li>
                                <li>"Find users from New York with 10k+ followers"</li>
                                <li>"Search for Restaurant business accounts"</li>
                                <li>"Show me top 20 verified accounts"</li>
                                <li>"Find users who posted with #fitness"</li>
                                <li>"Find women aged 25-30 from New York"</li>
                                <li>"Search for happy Asian users in Japan"</li>
                                <li>"Find men with #london hashtag"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Posts & Reels</h6>
                            <ul class="text-muted small">
                                <li>"Get the latest 10 posts from @username"</li>
                                <li>"Show me the most liked posts"</li>
                                <li>"Find posts mentioning 'product launch'"</li>
                                <li>"Get details for instagram.com/p/CxYz123"</li>
                                <li>"Find viral reels with 1M+ views"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Hashtags</h6>
                            <ul class="text-muted small">
                                <li>"How many posts use #photography?"</li>
                                <li>"What are the top 10 hashtags?"</li>
                                <li>"Search hashtags containing 'travel'"</li>
                                <li>"Compare #fitness vs #workout popularity"</li>
                            </ul>
                        </div>

                        <div class="col-md-6">
                            <h6 class="mt-3 text-primary">Locations</h6>
                            <ul class="text-muted small">
                                <li>"Find locations in Los Angeles"</li>
                                <li>"Get details for location ID 12345"</li>
                                <li>"Search for coffee shops in Manhattan"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Comments</h6>
                            <ul class="text-muted small">
                                <li>"Find comments containing 'great product'"</li>
                                <li>"Get all comments by user ID 12345"</li>
                                <li>"Search for negative feedback mentions"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Stories & Highlights</h6>
                            <ul class="text-muted small">
                                <li>"Get stories from @username"</li>
                                <li>"Show all highlights for user ID 12345"</li>
                                <li>"What highlights does @brand have?"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Music Tracks</h6>
                            <ul class="text-muted small">
                                <li>"Search for tracks by Drake"</li>
                                <li>"Find trending music tracks"</li>
                                <li>"Get details for track ID 12345"</li>
                            </ul>

                            <h6 class="mt-3 text-primary">Analytics</h6>
                            <ul class="text-muted small">
                                <li>"Calculate engagement rate for @username"</li>
                                <li>"Show database statistics"</li>
                                <li>"What's the avg likes for top influencers?"</li>
                            </ul>
                        </div>
                    </div>

                    <h5 class="mt-4 mb-3">Advanced Scenarios</h5>
                    <p class="text-muted small">Claude can combine multiple tools for complex analysis:</p>

                    <div class="card bg-light mb-3">
                        <div class="card-body py-2">
                            <strong class="small">Influencer Research</strong>
                            <p class="text-muted small mb-0">"Find fitness influencers in LA with 50k-500k followers, then show their engagement rates and recent posts"</p>
                        </div>
                    </div>

                    <div class="card bg-light mb-3">
                        <div class="card-body py-2">
                            <strong class="small">Competitor Analysis</strong>
                            <p class="text-muted small mb-0">"Compare engagement metrics between @brand1 and @brand2, analyze their most successful posts"</p>
                        </div>
                    </div>

                    <div class="card bg-light mb-3">
                        <div class="card-body py-2">
                            <strong class="small">Hashtag Strategy</strong>
                            <p class="text-muted small mb-0">"Find top hashtags in photography niche, then show me users who use them with highest engagement"</p>
                        </div>
                    </div>

                    <div class="card bg-light mb-3">
                        <div class="card-body py-2">
                            <strong class="small">Location-based Marketing</strong>
                            <p class="text-muted small mb-0">"Find popular restaurants in NYC, get their profiles and analyze which posts perform best"</p>
                        </div>
                    </div>

                    <div class="card bg-light mb-3">
                        <div class="card-body py-2">
                            <strong class="small">Content Analysis</strong>
                            <p class="text-muted small mb-0">"Search posts with 'sustainable fashion', analyze engagement patterns and identify top creators"</p>
                        </div>
                    </div>

                    <div class="card bg-light">
                        <div class="card-body py-2">
                            <strong class="small">Lead Generation by Region</strong>
                            <p class="text-muted small mb-0">"Find users from US, UK, Canada, Australia and EU countries with 5k-300k followers who have public email, location and bio"</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="py-2 py-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 v-show="!$state.context.id">
                        <router-link class="fw-bold text-primary-desat" :to="{path: '/registration'}">
                            Register
                        </router-link> and start using our service!
                    </h2>
                    <p class="fs-lg text-muted mb-7 mb-md-9">
                        Unlock millions of Instagram profiles, posts, and engagement metrics. Power your marketing research with real social media data.
                    </p>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import { highlightAll } from "../js/highlight";
import { EventBus } from '../bus'
import CodeCopy from './CodeCopy.vue'

export default {
    components: { CodeCopy },
    data() {
        return {
            userAccessKey: null
        }
    },
    computed: {
        accessKey() {
            return this.userAccessKey || 'YOUR_ACCESS_KEY'
        },
        mcpUrl() {
            return 'https://mcp.datalikers.com/mcp/'
        },
        oauthMcpUrl() {
            return 'https://mcp.datalikers.com/oauth'
        },
        legacyHeader() {
            return `Authorization: Bearer ${this.accessKey}`
        },
        configJson() {
            return `{
  "mcpServers": {
    "datalikers": {
      "url": "${this.mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${this.accessKey}"
      }
    }
  }
}`
        },
        claudeCodeCommand() {
            return `claude mcp add \\
  --transport http \\
  --header "Authorization: Bearer ${this.accessKey}" \\
  -s project \\
  datalikers ${this.mcpUrl}`
        },
        cursorConfig() {
            return `{
  "mcpServers": {
    "datalikers": {
      "url": "${this.mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${this.accessKey}"
      }
    }
  }
}`
        },
        zedConfig() {
            return `{
  "context_servers": {
    "datalikers": {
      "settings": {
        "url": "${this.mcpUrl}",
        "headers": {
          "Authorization": "Bearer ${this.accessKey}"
        }
      }
    }
  }
}`
        },
        codexConfig() {
            return `[mcp_servers.datalikers]
command = "npx"
args = [
  "-y",
  "mcp-remote",
  "${this.mcpUrl}",
  "--header",
  "Authorization: Bearer ${this.accessKey}"
]`
        },
        codexCommand() {
            return `codex mcp add datalikers \\
  -- npx -y mcp-remote ${this.mcpUrl} \\
  --header "Authorization: Bearer ${this.accessKey}"`
        }
    },
    methods: {
        fetchUserToken() {
            this.$services.Token().get().then(resp => {
                if (resp.body.data && resp.body.data.length > 0) {
                    // Prioritize token with "mcp" in the name (case-insensitive)
                    const mcpToken = resp.body.data.find(t =>
                        t.name && t.name.toLowerCase().includes('mcp')
                    )
                    if (mcpToken) {
                        this.userAccessKey = mcpToken.access_key
                    } else {
                        this.userAccessKey = resp.body.data[0].access_key
                    }
                }
            }).catch(err => {
                console.error('Failed to fetch token:', err)
            })
        },
        onContextLoaded(context) {
            if (context.id) {
                this.fetchUserToken()
            }
        }
    },
    created() {
        EventBus.on('context:loaded', this.onContextLoaded)
        // Check if context is already loaded with user
        if (this.$state.context.loaded && this.$state.context.id) {
            this.fetchUserToken()
        } else if (!this.$state.context.loaded) {
            // Request context if not yet loaded
            EventBus.emit('context:request')
        }
    },
    mounted() {
        this.$nextTick(highlightAll);
    },
    updated() {
        this.$nextTick(highlightAll);
    },
    beforeUnmount() {
        EventBus.off('context:loaded', this.onContextLoaded)
    }
}
</script>

<style scoped>
.mcp-screenshot {
    max-width: 600px;
}

/* Compact 2-col step cards (Claude.ai tab) */
.claude-web-steps .step-card {
    background: #2b3137;
    border: 1px solid #3a4048;
    border-radius: 12px;
    padding: 16px;
    height: 100%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.claude-web-steps .step-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.claude-web-steps .step-card-header h6 {
    margin: 0;
    font-weight: 600;
    font-size: 15px;
    color: #f5f7fa;
}

.claude-web-steps .step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6366f1;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    flex: 0 0 24px;
}

.claude-web-steps .step-card-text {
    color: #c4cad4;
    font-size: 13.5px;
    margin-bottom: 10px;
    line-height: 1.5;
}

.claude-web-steps .step-card-text strong {
    color: #f5f7fa;
}

.claude-web-steps .step-card-text a {
    color: #6ee7b7;
}

.claude-web-steps .mcp-screenshot {
    max-width: 100%;
}

.claude-web-steps .screenshot-content,
.claude-web-steps .screenshot-content.settings-view,
.claude-web-steps .screenshot-content.modal-view {
    min-height: 180px;
}

.claude-web-steps .step-card-final {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, #2b3137 100%);
    border-color: #4f51c4;
}

/* Mock screenshots styled after the real Claude.ai UI:
 * warm dark background (#1d1d1c / #262624), off-white text (#faf9f7),
 * coral CTA (#c96442), subtle hover/selected via 4-5% white overlays,
 * generous border radius (14-16px) and a system-font stack. */
.screenshot-placeholder {
    background: #1d1d1c;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    color: #faf9f7;
}

.screenshot-content {
    display: flex;
    min-height: 280px;
}

.screenshot-content.settings-view {
    min-height: 240px;
}

.screenshot-content.modal-view {
    min-height: 220px;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.45);
}

.screenshot-sidebar {
    width: 220px;
    background: #1a1a19;
    padding: 16px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.screenshot-logo {
    color: #faf9f7;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin-bottom: 24px;
    padding: 8px 0;
}

.screenshot-menu-item {
    height: 36px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 10px;
    margin-bottom: 6px;
}

.screenshot-profile {
    margin-top: auto;
    position: relative;
}

.screenshot-avatar {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #c96442 0%, #a3502f 100%);
    border-radius: 50%;
}

.screenshot-dropdown {
    position: absolute;
    bottom: 48px;
    left: 0;
    background: #262624;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 6px;
    min-width: 160px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.screenshot-dropdown-item {
    height: 30px;
    background: transparent;
    border-radius: 6px;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    color: #b5b3ad;
    font-size: 13px;
}

.screenshot-dropdown-item:last-child {
    margin-bottom: 0;
}

.screenshot-dropdown-item.highlight {
    background: rgba(255, 255, 255, 0.06);
    color: #faf9f7;
}

.screenshot-main {
    flex: 1;
    background: #1d1d1c;
}

.screenshot-settings-sidebar {
    width: 180px;
    background: #1a1a19;
    padding: 16px;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.screenshot-settings-item {
    padding: 9px 12px;
    color: #b5b3ad;
    font-size: 13px;
    border-radius: 8px;
    margin-bottom: 2px;
}

.screenshot-settings-item.highlight {
    background: rgba(255, 255, 255, 0.06);
    color: #faf9f7;
    font-weight: 500;
}

.screenshot-settings-main {
    flex: 1;
    padding: 24px;
}

.screenshot-title {
    color: #faf9f7;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin-bottom: 20px;
}

.screenshot-connector-list {
    margin-bottom: 20px;
}

.screenshot-connector {
    height: 44px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    margin-bottom: 8px;
}

.screenshot-btn {
    background: transparent;
    border: 1px dashed rgba(255, 255, 255, 0.15);
    color: #b5b3ad;
    padding: 12px 20px;
    border-radius: 10px;
    font-size: 14px;
    cursor: pointer;
    width: 100%;
    text-align: center;
}

.screenshot-btn.highlight {
    border: 1px solid #c96442;
    color: #c96442;
    background: rgba(201, 100, 66, 0.08);
}

.screenshot-modal {
    background: #262624;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    padding: 24px;
    min-width: 380px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
}

.screenshot-modal-title {
    color: #faf9f7;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin-bottom: 20px;
}

.screenshot-modal-consent .screenshot-consent-body {
    color: #d6d3cd;
    font-size: 13px;
    margin-bottom: 20px;
}

.screenshot-consent-line {
    margin-bottom: 12px;
}

.screenshot-consent-line strong {
    color: #faf9f7;
    font-weight: 600;
}

.screenshot-scope {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #faf9f7;
}

.screenshot-scope code {
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    background: rgba(255, 255, 255, 0.06);
    padding: 1px 6px;
    border-radius: 4px;
    color: #d6d3cd;
    font-size: 12px;
}

.screenshot-check {
    color: #6db97a;
    font-weight: 700;
}

.screenshot-input-group {
    margin-bottom: 20px;
}

.screenshot-input-group label {
    color: #b5b3ad;
    font-size: 12px;
    font-weight: 500;
    display: block;
    margin-bottom: 8px;
    text-transform: none;
    letter-spacing: 0;
}

.screenshot-input {
    background: #1a1a19;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 11px 13px;
    color: #faf9f7;
    font-size: 13px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.screenshot-modal-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.screenshot-btn-secondary {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #d6d3cd;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.screenshot-btn-primary {
    background: #c96442;
    border: 1px solid #c96442;
    color: #faf9f7;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.screenshot-btn-primary:hover {
    background: #b85738;
}

.screenshot-label {
    background: #1a1a19;
    color: #b5b3ad;
    padding: 11px 16px;
    font-size: 12px;
    text-align: center;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Sub-tabs (OAuth vs Legacy) inside the Claude.ai connector tab */
.mcp-auth-pills .nav-link {
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.1);
    color: var(--bs-gray-700, #4b5563);
    padding: 6px 14px;
    border-radius: 8px;
    margin-right: 8px;
    font-size: 14px;
}

.mcp-auth-pills .nav-link.active {
    background: var(--bs-primary, #6366f1);
    border-color: var(--bs-primary, #6366f1);
    color: #fff;
}

/* Terminal demo blocks */
.terminal-section {
    background: radial-gradient(ellipse at top, #1a1f2e 0%, #0f1117 70%);
    color: #e8edf6;
}

.terminal-section-title {
    color: #ffffff;
    font-weight: 700;
}

.terminal-section-subtitle {
    color: #9ca3b3;
    font-size: 1.05rem;
}

.terminal-section-footer {
    color: #6b7283;
}

.terminal-demo {
    background: #0f1117;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(15, 17, 23, 0.18);
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
    font-size: 13px;
    line-height: 1.55;
    border: 1px solid #1f2330;
}

.terminal-header {
    background: #181b24;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #1f2330;
}

.terminal-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
}

.terminal-dot.dot-red { background: #ff5f56; }
.terminal-dot.dot-yellow { background: #ffbd2e; }
.terminal-dot.dot-green { background: #27c93f; }

.terminal-title {
    margin-left: 12px;
    color: #8b93a7;
    font-size: 12px;
    letter-spacing: 0.02em;
    flex: 1;
    text-align: center;
    padding-right: 56px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.terminal-body {
    padding: 16px 18px 14px;
    color: #d8dde8;
}

.terminal-line {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 6px 0;
}

.terminal-prompt {
    color: #6ee7b7;
    font-weight: 600;
    flex: 0 0 auto;
}

.terminal-user {
    color: #e8edf6;
    word-break: break-word;
}

.terminal-tool {
    padding: 6px 0 10px 14px;
    color: #8b93a7;
    border-left: 2px solid #2a3142;
    margin: 4px 0 8px 6px;
    font-size: 12.5px;
    word-break: break-word;
}

.terminal-arrow {
    color: #a78bfa;
    margin-right: 6px;
}

.terminal-tool-name {
    color: #93c5fd;
    font-weight: 600;
}

.terminal-tool-args {
    color: #8b93a7;
}

.ta-str { color: #fbbf77; }
.ta-num { color: #f59e9e; }
.ta-bool { color: #c4b5fd; }

.terminal-result {
    margin-top: 6px;
    background: #0a0c12;
    border: 1px solid #1f2330;
    border-radius: 8px;
    overflow: hidden;
    overflow-x: auto;
}

.terminal-table {
    display: table;
    width: 100%;
    min-width: 640px;
    font-size: 12.5px;
}

.terminal-table .tr {
    display: table-row;
}

.terminal-table .td {
    display: table-cell;
    padding: 8px 12px;
    border-bottom: 1px solid #161924;
    vertical-align: middle;
    color: #cfd5e3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.terminal-table .tr.th .td {
    color: #6b7283;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: #11131c;
    border-bottom: 1px solid #1f2330;
}

.terminal-table .tr:last-child .td {
    border-bottom: none;
}

.terminal-table .tr.muted .td {
    color: #4b5060;
    font-style: italic;
}

.terminal-table .c-user { color: #6ee7b7; }
.terminal-table .c-name { color: #e8edf6; }
.terminal-table .c-num { color: #f9a8a8; text-align: right; font-variant-numeric: tabular-nums; }
.terminal-table .c-email { color: #93c5fd; }
.terminal-table .c-phone { color: #fbbf77; font-variant-numeric: tabular-nums; }
.terminal-table .c-city { color: #cfd5e3; }
.terminal-table .c-tag { text-align: center; }

.emo {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
}

.emo.happy { background: rgba(110, 231, 183, 0.15); color: #6ee7b7; }
.emo.neutral { background: rgba(148, 163, 184, 0.15); color: #cbd5e1; }
.emo.sad { background: rgba(147, 197, 253, 0.15); color: #93c5fd; }
.emo.angry { background: rgba(248, 113, 113, 0.15); color: #fca5a5; }
.emo.fear { background: rgba(167, 139, 250, 0.15); color: #c4b5fd; }
.emo.surprise { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }

.tier {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
}

.tier.mega { background: rgba(167, 139, 250, 0.18); color: #c4b5fd; }
.tier.macro { background: rgba(110, 231, 183, 0.15); color: #6ee7b7; }
.tier.mid { background: rgba(251, 191, 119, 0.15); color: #fbbf77; }

.badge-yes { color: #6ee7b7; font-weight: 600; }
.badge-no { color: #4b5060; }

.terminal-status {
    margin-top: 12px;
    color: #8b93a7;
    font-size: 12px;
}

.terminal-ok {
    color: #6ee7b7;
    font-weight: 700;
    margin-right: 6px;
}

@media (max-width: 600px) {
    .terminal-demo { font-size: 12px; }
    .terminal-table { min-width: 520px; }
    .terminal-title { display: none; }
}
</style>
